import {
  put,
  takeEvery,
  all,
  call,
  select,
  delay,
  takeLatest
} from 'redux-saga/effects'

import * as Actions from 'actions/contact_management'
import * as AppActions from 'actions/app'
import * as Selectors from 'selectors'
import * as Api from 'api/contacts_management'

// helpers

export const normalizeSmartSearchQuery = query => (query || '').trim()

const distributionListSortFieldMapping = {
  createdAt: 'created_at',
  updatedAt: 'updated_at'
}

const getDistributionListApiSettings = dataGridSettings => {
  const paginationModel = dataGridSettings.get('paginationModel')
  const sortModel = dataGridSettings.get('sortModel')
  const sort = sortModel.first()
  const sortField = sort?.get('field')
  const sortOrder = sort?.get('sort')

  return {
    // Autocomplete searches should always start with the first result page.
    page: 1,
    limit: paginationModel.get('pageSize'),
    sortBy: distributionListSortFieldMapping[sortField] || sortField,
    sortOrder
  }
}

export function* fetchNextPage({ page, limit }) {
  const body = {
    limit,
    page
  }

  const result = yield call(Api.fetchContacts, body)
  yield put(Actions.updateContacts(result.contacts))

  return result.contacts.length
}

export function* fetchAllContacts({ limit, page }) {
  let size = yield fetchNextPage({ page, limit })
  let nextPage = page

  yield put(Actions.fetchMoreContactsStart())
  while (size > 0) {
    nextPage += 1
    size = yield fetchNextPage({ page: nextPage, limit })
    yield delay(50)
  }
  yield put(Actions.fetchMoreContactsStop())

  const lastPage = nextPage - 1

  return lastPage
}

export function* fetchNextDistributionListPage({ page, limit }) {
  const body = {
    limit,
    page
  }

  const result = yield call(Api.fetchDistributionLists, body)
  yield put(Actions.updateDistributionLists(result.distributionLists))

  return result
}

export function* fetchAllDistributionLists({ limit }) {
  const firstResult = yield fetchNextDistributionListPage({ page: 1, limit })
  yield put(Actions.setTotalDistributionLists(firstResult.total))

  let size = firstResult.distributionLists.length
  let page = 1

  yield put(Actions.fetchMoreDistributionListsStart())
  while (size > 0) {
    page += 1
    const result = yield fetchNextDistributionListPage({ page, limit })
    size = result.distributionLists.length
    yield delay(50)
  }
  yield put(Actions.fetchMoreDistributionListsStop())
}

// generators

export function* fetchContacts() {
  try {
    const page = yield select(Selectors.getCmUiContactsPage)
    const size = yield select(Selectors.getCmDgPageSize)
    const result = yield call(Api.countContacts, {})
    yield put(Actions.setTotalContacts(result.total))

    const lastPage = yield fetchAllContacts({
      limit: size * 4,
      page
    })

    yield put(Actions.setContactsPage(lastPage))
    yield put(Actions.updateContactRanking())
    yield put(Actions.fetchContactsSuccess())
  } catch (error) {
    yield put(Actions.fetchContactsError(error))
    yield put(
      AppActions.showAppMessage({
        success: false
      })
    )
  }
}

export function* searchContacts({ payload }) {
  const body = {
    filter: payload.filter,
    page: 1,
    limit: payload.limit
  }
  try {
    const result = yield call(Api.fetchContacts, body)
    yield put(Actions.searchContactsSuccess(result))
  } catch (error) {
    yield put(Actions.searchContactsError(error))
  }
}

export function* fetchDistributionLists() {
  try {
    const dataGridSettings = yield select(Selectors.getCmDlDgSettings)

    const limit = dataGridSettings.getIn(['paginationModel', 'pageSize']) * 4

    yield fetchAllDistributionLists({ limit })

    yield put(Actions.fetchDistributionListsSuccess())
  } catch (error) {
    yield put(Actions.fetchDistributionListsError(error))
    yield put(
      AppActions.showAppMessage({
        success: false
      })
    )
  }
}

export function* searchDistributionList({ payload }) {
  const dataGridSettings = yield select(Selectors.getCmDlDgSettings)
  const { page, limit, sortBy, sortOrder } = getDistributionListApiSettings(dataGridSettings)

  let body = {
    filter: payload.filter,
    page,
    limit
  }

  if (sortBy && sortOrder) {
    body = {
      ...body,
      sort_by: sortBy,
      sort_order: sortOrder
    }
  }

  try {
    const result = yield call(Api.fetchDistributionLists, body)
    yield put(Actions.searchDistributionListSuccess(result))
  } catch (error) {
    yield put(Actions.searchDistributionListError(error))
  }
}

export function* smartSearch({ payload } = {}) {
  let query = payload

  if (query === undefined) {
    query = yield select(Selectors.getCmUiQuery)
  }

  const smartSearchFilter = normalizeSmartSearchQuery(query)

  yield put(Actions.clearSmartSearchFilterIds())
  yield put(Actions.fetchSmartSearchContactIdsStart(smartSearchFilter))
  yield put(Actions.fetchSmartSearchDistributionListIdsStart(smartSearchFilter))
}

export function* fetchSmartSearchContactIds({ payload: query }) {
  try {
    const smartSearchFilter = normalizeSmartSearchQuery(query)

    if (!smartSearchFilter) {
      yield put(Actions.fetchSmartSearchContactIdsSuccess([]))

      return
    }

    const result = yield call(Api.fetchContactIds, { smart_search_filter: smartSearchFilter })
    yield put(Actions.fetchSmartSearchContactIdsSuccess(result.contacts || []))
  } catch (error) {
    yield put(Actions.fetchSmartSearchContactIdsError(error))
    yield put(
      AppActions.showAppMessage({
        success: false
      })
    )
  }
}

export function* fetchSmartSearchDistributionListIds({ payload: query }) {
  try {
    const smartSearchFilter = normalizeSmartSearchQuery(query)

    if (!smartSearchFilter) {
      yield put(Actions.fetchSmartSearchDistributionListIdsSuccess([]))

      return
    }

    const result = yield call(Api.fetchDistributionListIds, { smart_search_filter: smartSearchFilter })

    yield put(Actions.fetchSmartSearchDistributionListIdsSuccess(result.distributionLists || []))
  } catch (error) {
    yield put(Actions.fetchSmartSearchDistributionListIdsError(error))
    yield put(
      AppActions.showAppMessage({
        success: false
      })
    )
  }
}

export function* fetchAll() {
  // Reset everything except the app bar query, which should remain visible while regular data is reloaded.
  const query = yield select(Selectors.getCmUiQuery)
  const detailPageContactId = yield select(Selectors.getCmUiDetailPageContactId)
  yield put(Actions.resetState())
  yield put(Actions.setQuery(query))

  if (detailPageContactId) {
    yield put(Actions.setDetailPageContactId(detailPageContactId))
  }

  yield put(Actions.fetchContactsStart())
  yield put(Actions.fetchDistributionListsStart())
}

// taker functions

export function* watchFetchContactsStart() {
  yield takeLatest(Actions.fetchContactsStart, fetchContacts)
}

export function* watchFetchDistributionListsStart() {
  yield takeLatest(
    Actions.fetchDistributionListsStart,
    fetchDistributionLists
  )
}

export function* watchSmartSearchStart() {
  yield takeLatest(Actions.smartSearchStart, smartSearch)
}

export function* watchFetchSmartSearchContactIdsStart() {
  yield takeLatest(Actions.fetchSmartSearchContactIdsStart, fetchSmartSearchContactIds)
}

export function* watchFetchSmartSearchDistributionListIdsStart() {
  yield takeLatest(
    Actions.fetchSmartSearchDistributionListIdsStart,
    fetchSmartSearchDistributionListIds
  )
}

export function* watchSearchContactsStart() {
  yield takeEvery(Actions.searchContactsStart, searchContacts)
}

export function* watchSearchDistributionListStart() {
  yield takeEvery(
    Actions.searchDistributionListStart,
    searchDistributionList
  )
}

export function* watchFetchAll() {
  yield takeEvery(Actions.fetchAll, fetchAll)
}

export default function* searchSaga() {
  yield all([
    watchFetchContactsStart(),
    watchFetchDistributionListsStart(),
    watchSmartSearchStart(),
    watchFetchSmartSearchContactIdsStart(),
    watchFetchSmartSearchDistributionListIdsStart(),
    watchFetchAll(),
    watchSearchContactsStart(),
    watchSearchDistributionListStart()
  ])
}
